import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { GeocodingService, GeocodingSugestionResult } from '@mobiletract/esg-calculator-service-angular-client';

@Component({
    selector: 'app-location-search',
    templateUrl: './location-search.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LocationSearchComponent),
            multi: true
        }
    ],
    standalone: false
})
export class LocationSearchComponent implements ControlValueAccessor {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() companyId: string | null = null;
  @Output() locationSelected = new EventEmitter<GeocodingSugestionResult>();

  suggestions: GeocodingSugestionResult[] = [];
  selectedLocation: GeocodingSugestionResult | null = null;
  searchValue: string = '';
  disabled: boolean = false;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private geocodingService: GeocodingService,
    private translate: TranslateService
  ) {}

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value && typeof value === 'object') {
      this.selectedLocation = value;
      this.searchValue = value.name || value.formattedAddress || '';
    } else if (typeof value === 'string') {
      this.searchValue = value;
      this.selectedLocation = null;
    } else {
      this.searchValue = '';
      this.selectedLocation = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Search functionality
  onSearch(event: any): void {
    const query = event.query;
    if (query && query.length >= 3) {
      this.searchLocations(query).then(suggestions => {
        this.suggestions = suggestions;
      });
    } else {
      this.suggestions = [];
    }
  }

  onLocationSelect(event: any): void {
    const suggestion: GeocodingSugestionResult = event.value || event;
    if (suggestion) {
      this.selectedLocation = suggestion;
      this.searchValue = suggestion.name || suggestion.formattedAddress || '';
      
      // Emit the complete location object
      this.onChange(suggestion);
      this.locationSelected.emit(suggestion);
      this.onTouched();
    }
  }

  onClearSelection(): void {
    this.selectedLocation = null;
    this.searchValue = '';
    this.onChange(null);
    this.locationSelected.emit(null as any);
  }

  onInputChange(value: string): void {
    this.searchValue = value;
    if (!value) {
      this.onClearSelection();
    }
  }

  private searchLocations(query: string): Promise<Array<GeocodingSugestionResult>> {
    if (!this.companyId) {
      return Promise.resolve([]);
    }

    return lastValueFrom(
      this.geocodingService.getGeoAutoComplete(query, "en")
    ).then((results: any) => {
      if (Array.isArray(results)) {
        return results.map((result: any) => ({
          name: result.name || result.display_name || query,
          lat: result.lat,
          lon: result.lon,
          country: result.country || '',
          countrycode: result.countrycode || '',
          city: result.city,
          district: result.district,
          locality: result.locality,
          state: result.state,
          postcode: result.postcode,
          street: result.street,
          housenumber: result.housenumber,
          county: result.county,
          type: result.type,
          osmId: result.osmId,
          formattedAddress: result.formattedAddress || this.buildFormattedAddress(result)
        }));
      }
      return [];
    }).catch(() => {
      return [];
    });
  }

  private buildFormattedAddress(result: any): string {
    const parts: string[] = [];
    
    if (result.street) {
      parts.push(result.street);
      if (result.housenumber) {
        parts[parts.length - 1] += ` ${result.housenumber}`;
      }
    }
    
    if (result.city) parts.push(result.city);
    if (result.county) parts.push(result.county);
    if (result.state) parts.push(result.state);
    if (result.postcode) parts.push(result.postcode);
    if (result.country) parts.push(result.country);
    
    return parts.join(', ');
  }

  // Helper methods for display
  getLocationTypeIcon(type: string): string {
    const iconMap: { [key: string]: string } = {
      'house': 'pi-home',
      'building': 'pi-building',
      'locality': 'pi-map-marker',
      'district': 'pi-map',
      'city': 'pi-globe',
      'county': 'pi-map',
      'state': 'pi-flag',
      'country': 'pi-flag-fill',
      'postcode': 'pi-envelope',
      'street': 'pi-directions',
      'amenity': 'pi-star',
      'shop': 'pi-shopping-cart',
      'tourism': 'pi-camera',
      'leisure': 'pi-palette',
      'natural': 'pi-sun',
      'landuse': 'pi-th-large',
      'place': 'pi-map-marker',
      'administrative': 'pi-cog',
      'highway': 'pi-car',
      'railway': 'pi-forward',
      'waterway': 'pi-tint',
      'boundary': 'pi-stop'
    };
    
    return iconMap[type] || 'pi-map-marker';
  }

  getLocationTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      'house': 'text-blue-600',
      'building': 'text-purple-600',
      'locality': 'text-green-600',
      'district': 'text-orange-600',
      'city': 'text-red-600',
      'county': 'text-indigo-600',
      'state': 'text-pink-600',
      'country': 'text-gray-600',
      'postcode': 'text-yellow-600',
      'street': 'text-teal-600',
      'amenity': 'text-amber-600',
      'shop': 'text-cyan-600',
      'tourism': 'text-lime-600',
      'leisure': 'text-emerald-600',
      'natural': 'text-green-500',
      'landuse': 'text-brown-600',
      'place': 'text-violet-600',
      'administrative': 'text-slate-600',
      'highway': 'text-gray-700',
      'railway': 'text-blue-700',
      'waterway': 'text-blue-400',
      'boundary': 'text-red-700'
    };
    
    return colorMap[type] || 'text-gray-500';
  }

  formatLocationDetails(location: GeocodingSugestionResult): string {
    const parts: string[] = [];
    
    if (location.street) {
      parts.push(location.street);
      if (location.housenumber) {
        parts[parts.length - 1] += ` ${location.housenumber}`;
      }
    }
    
    if (location.city && location.city !== location.name) {
      parts.push(location.city);
    }
    
    if (location.district && location.district !== location.name && location.district !== location.city) {
      parts.push(location.district);
    }
    
    if (location.county && location.county !== location.city) {
      parts.push(location.county);
    }
    
    if (location.state) {
      parts.push(location.state);
    }
    
    if (location.postcode) {
      parts.push(location.postcode);
    }
    
    if (location.country && location.country !== location.state) {
      parts.push(location.country);
    }
    
    return parts.join(', ');
  }

  formatLocationName(location: GeocodingSugestionResult): string {
    return location.name || location.formattedAddress || 'Unknown location';
  }
}
